// main.js – boot Firebird in a Worker, load the PK3 into it, run the loop.
//
// Per frame the browser does two queries:
//   SELECT * FROM q3_tic(...)         – advance the game by N tics
//   SELECT * FROM frame_all(...)      – the frame: the polygons on screen, the models in view,
//                                       what to play and where, the effects, the console
// and then paints. All game state lives in Firebird tables.

import { FirebirdBrowser } from 'firebird-wasm/browser';
import schemaSql from '../sql/schema.sql';
import physicsSql from '../sql/physics.sql';
import gameSql from '../sql/game.sql';
import playerSql from '../sql/player.sql';
import waypointsSql from '../sql/waypoints.sql';
import botsSql from '../sql/bots.sql';
import renderSql from '../sql/render.sql';
import { Pk3, PakSet } from './pk3.js';
import { createSchema, loadResources, loadMap, buildWaypoints, setView, setRotation } from './loader.js';
import { loadShaders } from './shader.js';
import { Renderer } from './renderer.js';
import { GLRenderer } from './renderer-gl.js';
import { Hud } from './hud.js';
import { FrameState, drawScene, firstPersonView, zoomedFov, fovY, ZOOM_FOV, underwaterFov, findPortals, portalView, mirrorView, portalFade } from './scene.js';
import { Q3Audio, weaponLoop } from './audio.js';

const $ = (id) => document.getElementById(id);
const canvas = $('screen');
const glCanvas = $('glscreen');
const overlayCanvas = $('overlay');
const wrap = $('screen-wrap');   // the mouse and touch act on the wrapper: whichever canvas is showing
const statusEl = $('status');
const statsEl = $('stats');
const TIC_MS = 50;

let db, pak, res, renderer, hud;
let map = null;          // { name, bsp, unlinked, portals }
const portalState = new FrameState();   // parses the portal camera's rows (FRAME_PORTAL)
let last = null;         // last Q3_TIC row
let prev = null;         // the one before: the frame between two tics is painted between the two states
let prevPose = null, curPose = null;   // the entities' and brush models' poses at those tics
let frameAt = 0;         // when the last frame was painted
let lastTics = 1;        // how many tics the last q3_tic call ran (the displacement prev → last covers them)
let duckChange = 0, duckTime = -1e9;   // the eye's last crouch or stand step, and when it happened
let running = false;
let paused = false;
let lastTic = 0;
let lastSoundId = 0;
let lastFxId = 0;
let frameNo = 0;
let scores = [];
const state = new FrameState();
const settings = { map: 'q3dm1', detail: 'medium', sfx: 70, music: 40, musicMode: 'tracks', skill: 2, bots: 3, fov: 90, renderer: 'fast', brightness: 4, predict: true, fraglimit: 20, timelimit: 10, rotate: true, name: 'Player', model: 'sarge/default', rail: 4, gametype: 'ffa', team: 'auto' };
try { Object.assign(settings, JSON.parse(localStorage.getItem('firebird-quake3:settings') || '{}')); } catch { /* defaults */ }
const saveSettings = () => { try { localStorage.setItem('firebird-quake3:settings', JSON.stringify(settings)); } catch { /* ignore */ } };
const viewWidth = () => (settings.detail === 'high' ? 640 : settings.detail === 'low' ? 160 : 320);
const viewHeight = () => (settings.detail === 'high' ? 480 : settings.detail === 'low' ? 120 : 240);
const audio = new Q3Audio();
audio.setVolume(settings.sfx / 100);
audio.setMusicVolume(settings.music / 100);
audio.musicMode = settings.musicMode;
for (const ev of ['keydown', 'pointerdown', 'touchstart']) window.addEventListener(ev, () => audio.unlock(), { capture: true });
document.addEventListener('visibilitychange', () => audio.suspend(document.hidden));
const perf = { tic: 0, faces: 0, draw: 0, rows: 0, graph: 0 };

function setStatus(msg, isError = false) {
  statusEl.textContent = msg;
  statusEl.classList.toggle('error', isError);
  statusEl.hidden = !msg;
}

// ── input ────────────────────────────────────────────────────────────────
const keys = new Set();
let mouseYaw = 0, mousePitch = 0;
let fireClick = false;
let impulse = 0;
let scoreboard = false;
// +zoom (Z or the right mouse button): when it went down or up, and the field of view of the last frame
let zoomed = false, zoomAt = -1e9, curFov = 90;
const zoom = (on) => { if (on !== zoomed) { zoomed = on; zoomAt = performance.now(); } };
const GAME_KEYS = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space', 'KeyE',
  'ControlLeft', 'ControlRight', 'ShiftLeft', 'ShiftRight', 'Tab', 'Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6', 'Digit7',
  'Digit8', 'Digit9', 'Digit0', 'KeyF', 'KeyG', 'Comma', 'Period', 'PageUp', 'PageDown', 'Slash', 'Enter', 'KeyH', 'KeyC', 'KeyZ']);
window.addEventListener('keydown', (e) => {
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;
  if (!running) return;
  if (GAME_KEYS.has(e.code)) e.preventDefault();
  keys.add(e.code);
  if (e.code.startsWith('Digit')) { const n = Number(e.code.slice(5)); if (n >= 1 && n <= 9) impulse = n; }
  if (e.code === 'Slash') impulse = 12;
  if (e.code === 'KeyG') impulse = 99;
  if (e.code === 'Enter' || e.code === 'KeyH') impulse = 13;
  if (e.code === 'Tab') scoreboard = true;
  if (e.code === 'KeyZ') zoom(true);
  if (e.code === 'KeyP' || e.code === 'Pause') paused = !paused;
});
window.addEventListener('keyup', (e) => { keys.delete(e.code); if (e.code === 'Tab') scoreboard = false; if (e.code === 'KeyZ') zoom(false); });
window.addEventListener('blur', () => { keys.clear(); zoom(false); });
wrap.addEventListener('click', () => {
  if (running && document.pointerLockElement !== wrap) wrap.requestPointerLock?.()?.catch?.(() => {});
});
wrap.addEventListener('mousedown', (e) => {
  if (document.pointerLockElement !== wrap) return;
  if (e.button === 0) fireClick = true;
  if (e.button === 2) zoom(true);
});
window.addEventListener('mouseup', (e) => { if (e.button === 0) fireClick = false; if (e.button === 2) zoom(false); });
wrap.addEventListener('contextmenu', (e) => e.preventDefault());
window.addEventListener('mousemove', (e) => {
  if (document.pointerLockElement === wrap) {
    // zoomed, the mouse slows with the view (cg.zoomSensitivity = fov_y / 75)
    const sens = zoomed ? fovY(curFov, viewWidth(), viewHeight()) / 75 : 1;
    mouseYaw -= e.movementX * 0.15 * sens;
    mousePitch += e.movementY * 0.15 * sens;
  }
});
window.addEventListener('wheel', (e) => { if (document.pointerLockElement === wrap) impulse = e.deltaY > 0 ? 12 : 14; });
// touch: left half moves, right half looks, tap fires
const touch = { move: null, look: null };
wrap.addEventListener('touchstart', (e) => {
  const r = wrap.getBoundingClientRect();
  for (const t of e.changedTouches) {
    const rec = { id: t.identifier, x: t.clientX, y: t.clientY, dx: 0, dy: 0, t: performance.now() };
    if (t.clientX - r.left < r.width / 2) touch.move = rec; else touch.look = rec;
  }
  e.preventDefault();
}, { passive: false });
wrap.addEventListener('touchmove', (e) => {
  for (const t of e.changedTouches) for (const k of ['move', 'look']) {
    const rec = touch[k];
    if (rec && rec.id === t.identifier) {
      if (k === 'look') { mouseYaw -= (t.clientX - rec.x - rec.dx) * 0.4; mousePitch += (t.clientY - rec.y - rec.dy) * 0.4; }
      rec.dx = t.clientX - rec.x; rec.dy = t.clientY - rec.y;
    }
  }
  e.preventDefault();
}, { passive: false });
wrap.addEventListener('touchend', (e) => {
  for (const t of e.changedTouches) for (const k of ['move', 'look']) {
    const rec = touch[k];
    if (rec && rec.id === t.identifier) {
      if (k === 'look' && Math.abs(rec.dx) < 10 && Math.abs(rec.dy) < 10 && performance.now() - rec.t < 250) fireClick = 'tap';
      if (k === 'move' && Math.abs(rec.dx) < 10 && Math.abs(rec.dy) < 10 && performance.now() - rec.t < 250) keys.add('TapJump');
      touch[k] = null;
    }
  }
  e.preventDefault();
}, { passive: false });

function readInput(tics) {
  const k = (c) => keys.has(c);
  let fwd = (k('KeyW') || k('ArrowUp') ? 1 : 0) - (k('KeyS') || k('ArrowDown') ? 1 : 0);
  let side = (k('KeyD') || k('Period') ? 1 : 0) - (k('KeyA') || k('Comma') ? 1 : 0);
  const turnKeys = (k('ArrowLeft') ? 1 : 0) - (k('ArrowRight') ? 1 : 0);
  const lookKeys = (k('PageDown') ? 1 : 0) - (k('PageUp') ? 1 : 0);
  const run = k('ShiftLeft') || k('ShiftRight') ? 0 : 1;     // always run; shift walks
  if (touch.move) {
    fwd = Math.max(-1, Math.min(1, -touch.move.dy / 40));
    side = Math.max(-1, Math.min(1, touch.move.dx / 40));
  }
  const yaw = turnKeys * 7 * tics + mouseYaw;
  const pitch = lookKeys * 5 * tics + mousePitch;
  mouseYaw = 0; mousePitch = 0;
  const fire = k('ControlLeft') || k('ControlRight') || k('KeyF') || fireClick ? 1 : 0;
  if (fireClick === 'tap') fireClick = false;
  // upmove, as Quake III has it: 1 jumps, -1 crouches (C, Quake III's +movedown key)
  const jump = k('Space') || k('KeyE') || k('TapJump') ? 1 : k('KeyC') ? -1 : 0;
  keys.delete('TapJump');
  const imp = impulse;
  impulse = 0;
  return [tics, fwd, side, yaw, pitch, fire, jump, run, imp];
}

// ── maps ─────────────────────────────────────────────────────────────────
// The arenas: the base pak's, the map packs the site hosts (pak/maps/index.json: OpenArena's and the
// OpenArena Community Map-Pack's arenas, one small pk3 each, fetched when picked: scripts/fetch-maps.mjs)
// and the maps of the pk3s picked from disk. Each pk3 goes on top of the others (PakSet).
let catalog = [];          // [{ map, title, pack, file, bytes }] from pak/maps/index.json
const local = new Map();   // map → the pk3 file it came from (picked from disk)
let basePak = null;        // the game's pak (the demo's pak0.pk3)
const localPaks = [];      // the pk3s picked from disk, in the order picked
const fetchedPaks = new Map();   // a hosted map pack's file → its Pk3, kept once fetched

/** The paks as a map needs them: the base, the picked ones, and the map's own pack on top (one hosted pack at
 *  a time, so one arena's shaders never dress another's). The shaders are read again (a later file wins) and
 *  the painters forget the pictures they have, which may be another pak's now. */
function stackPaks(top = null) {
  const layers = [basePak, ...localPaks, ...(top ? [top] : [])];
  if (layers.length === pak.paks.length && layers.every((p, i) => p === pak.paks[i])) return;
  pak.reset(layers);
  const shaders = loadShaders(pak);
  res.shaders.clear();
  for (const [k, v] of shaders) res.shaders.set(k, v);
  renderer?.resetCaches?.();
}

/** The Arena menu: the base pak's maps, then each pack's, then the picked files' */
function fillMapMenu() {
  const base = basePak.mapNames();
  const opt = (m, title) => `<option value="${m}">${title && title !== m ? `${m} – ${title}` : m}</option>`;
  const groups = [[basePak.label ?? 'pak0.pk3', base.map((m) => opt(m))]];
  const packs = new Map();
  for (const c of catalog) {
    if (!packs.has(c.pack)) packs.set(c.pack, []);
    packs.get(c.pack).push(opt(c.map, c.title));
  }
  for (const [p, opts] of packs) groups.push([`${p} (fetched when picked)`, opts]);
  if (local.size) groups.push(['Your pk3s', [...local.keys()].map((m) => opt(m))]);
  $('map').innerHTML = groups.map(([label, opts]) => `<optgroup label="${label.replace(/"/g, '')}">${opts.join('')}</optgroup>`).join('');
}

/** Have a map's files: a hosted pack's pk3 is fetched once and put on top; the rotation goes through the
 *  arenas of the same source */
async function ensureMap(name) {
  const c = basePak.has(`maps/${name}.bsp`) || local.has(name) ? null : catalog.find((m) => m.map === name) ?? null;
  let top = null;
  if (c) {
    top = fetchedPaks.get(c.file);
    if (!top) {
      setStatus(`Downloading ${name} (${c.pack}, ${(c.bytes / 1048576).toFixed(1)} MB)…`);
      const resp = await fetch(new URL(`./pak/maps/${c.file}`, location.href));
      if (!resp.ok) throw new Error(`could not fetch ${c.file} (${resp.status})`);
      top = new Pk3(await resp.arrayBuffer());
      top.label = c.pack;
      fetchedPaks.set(c.file, top);
    }
  }
  stackPaks(top);
  if (!pak.has(`maps/${name}.bsp`)) throw new Error(`no map ${name}`);
  const rotation = c ? catalog.filter((m) => m.pack === c.pack).map((m) => m.map)
    : local.has(name) ? [...local.keys()] : basePak.mapNames();
  await setRotation(db, rotation);
}

async function startMap(name) {
  running = false;
  await ensureMap(name);
  setStatus(`Loading ${name} into Firebird…`);
  const t0 = performance.now();
  const bsp = await loadMap(db, pak, res, name, { skill: settings.skill, bots: settings.bots, link: false, fraglimit: settings.fraglimit, timelimit: settings.timelimit, warmup: 4,
    gametype: { team: 3, tourney: 1 }[settings.gametype] ?? 0, team: { red: 1, blue: 2 }[settings.team] ?? 0 });   // "prepare to fight", three, two, one, "fight!"
  map = { name, bsp, unlinked: 1, portals: findPortals(bsp, res.look) };
  await setPlayerName();
  fillModelMenu();
  await setPlayerModel();
  await setPlayerColor();
  renderer.setResources(res);
  renderer.particles = [];
  renderer.meshCache?.clear();
  state.explosions = []; state.beams = [];
  state.selfId = (await db.query('SELECT player_ent() pe FROM rdb$database')).rows[0].PE;   // whose lightning follows the view
  const g = (await db.query('SELECT sky, music FROM game')).rows[0];
  map.sky = g.SKY;
  renderer.setSky(g.SKY);
  // the looped target_speakers, and the movers' looping sounds (a mover's "noise"), at the middle of the brush
  const speakers = (await db.query(`SELECT id, x, y, z, noise1, speed, height, count_ FROM ents WHERE classname = 'target_speaker' AND BIN_AND(spawnflags, 3) <> 0 AND noise1 IS NOT NULL
    UNION ALL SELECT id, x + (minx + maxx) / 2, y + (miny + maxy) / 2, z + (minz + maxz) / 2, loop_sound, 1, 1, 1 FROM ents WHERE loop_sound IS NOT NULL`, [], arr)).rows;
  audio.setSpeakers(speakers);
  audio.playMusic(g.MUSIC);
  const { rows } = await db.query('SELECT MAX(id) m FROM sound_events');
  lastSoundId = rows[0].M ?? 0;
  lastFxId = 0;
  console.log(`[firebird-quake3] ${name} loaded in ${(performance.now() - t0).toFixed(0)} ms`);
  setStatus('');
  $('mapname').textContent = name;
  $('map').value = name;
  lastTic = performance.now() - TIC_MS;
  prev = null; prevPose = null; curPose = null; frameAt = 0;
  running = true;
  refreshBotMenus().catch((err) => console.error(err));
}

// ── the loop ─────────────────────────────────────────────────────────────
function nextFrame() {
  let done = false;
  const go = () => { if (!done) { done = true; frame(); } };
  requestAnimationFrame(go);
  setTimeout(go, 60);
}

const arr = { rowMode: 'array' };

// ── between two tics ─────────────────────────────────────────────────────
// The game runs at 20 Hz; the painter runs at the display's rate. A frame between two tics shows the
// world interpolated between the last two states (CG_CalcEntityLerpPositions), one tic behind, and the
// player predicted ahead of the last state (CG_PredictPlayerState, by extrapolation rather than by
// re-running the move: the move is SQL), with the view angles live from the mouse, so neither the
// look nor your own motion waits for a tic.
const lerp = (a, b, k) => a + (b - a) * k;
const lerpAngle = (a, b, k) => a + (((b - a + 540) % 360) - 180) * k;
const SNAP = 200;   // a jump this long in one tic is a teleport, not a move

/** The poses of a tic's frame: entity id → [x, y, z, pitch, yaw, roll]; brush models by their origin and angles. */
function poseOf(fr) {
  const ents = new Map(), brush = new Map(), rot = new Map();
  for (const e of fr.ents) ents.set(e.id, [e.x, e.y, e.z, e.pitch, e.yaw, e.roll]);
  for (const f of fr.faces) if (f[1] && !brush.has(f[1])) brush.set(f[1], [f[2], f[3], f[4]]);
  for (const [id, a] of state.brushAngles) rot.set(id, a);
  return { ents, brush, rot };
}

/** Move this frame's entities back toward where they were at the previous tic, by 1 − alpha. */
function interpolateFrame(fr, alpha) {
  if (!prevPose || alpha >= 1) return;
  for (const e of fr.ents) {
    const p = prevPose.ents.get(e.id);
    if (!p || Math.hypot(e.x - p[0], e.y - p[1], e.z - p[2]) > SNAP) continue;
    e.x = lerp(p[0], e.x, alpha); e.y = lerp(p[1], e.y, alpha); e.z = lerp(p[2], e.z, alpha);
    e.pitch = lerpAngle(p[3], e.pitch, alpha); e.yaw = lerpAngle(p[4], e.yaw, alpha); e.roll = lerpAngle(p[5], e.roll, alpha);
  }
  if (!fr.sqlProjected) for (const f of fr.faces) {
    const p = f[1] && prevPose.brush.get(f[1]);
    if (!p || Math.hypot(f[2] - p[0], f[3] - p[1], f[4] - p[2]) > SNAP) continue;
    f[2] = lerp(p[0], f[2], alpha); f[3] = lerp(p[1], f[3], alpha); f[4] = lerp(p[2], f[4], alpha);
  }
  for (const [id, a] of state.brushAngles) {
    const p = prevPose.rot.get(id);
    if (p) state.brushAngles.set(id, [lerpAngle(p[0], a[0], alpha), lerpAngle(p[1], a[1], alpha), lerpAngle(p[2], a[2], alpha)]);
  }
}

/** The tic row as the painter sees it this frame: the eye predicted `alpha` of a tic past the last
 *  state along the last tic's motion, the clock between the two tics, the angles live. The frame
 *  query clamps a predicted eye with a trace and returns the eye it used (row kind 10). */
function viewRow(alpha, now) {
  const k = (c) => keys.has(c);
  const frac = Math.min(1, (now - lastTic) / TIC_MS);
  const yawLive = mouseYaw + ((k('ArrowLeft') ? 1 : 0) - (k('ArrowRight') ? 1 : 0)) * 7 * frac;
  const pitchLive = mousePitch + ((k('PageDown') ? 1 : 0) - (k('PageUp') ? 1 : 0)) * 5 * frac;
  if (last.MATCH_OVER || last.FOLLOW_NAME) return { ...last };   // the intermission camera and the eyes of the one followed do not turn with the mouse
  // (a turning mover under us turns the view on through the tic as it did through the last one)
  const v = { ...last, YAW: (last.YAW + yawLive + (last.MOVER_YAW || 0) * frac) % 360, PITCH: Math.max(-89, Math.min(89, last.PITCH + pitchLive)) };
  if (!prev || alpha >= 1 || Math.hypot(last.PX - prev.PX, last.PY - prev.PY, last.PZ - prev.PZ) >= SNAP) return v;
  v.TIME_ = lerp(prev.TIME_, last.TIME_, alpha);
  const duckLeft = Math.max(0, 1 - (now - duckTime) / 100);
  if (duckLeft > 0) v.VIEW_Z += duckChange * duckLeft;
  if (last.DEAD || !settings.predict) return v;
  // the displacement of one tic, carried on for alpha of the next; with no move key down on the
  // ground, friction is stopping us, so carry on half as far (an overshoot that snaps back is worse
  // than a little lag)
  const moving = touch.move || ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'Comma', 'Period'].some(k);
  const kk = (alpha / Math.max(1, lastTics)) * (moving || !last.ONGROUND ? 1 : 0.5);
  v.PX = last.PX + (last.PX - prev.PX) * kk;
  v.PY = last.PY + (last.PY - prev.PY) * kk;
  // up and down only in the air on both tics: a landing would otherwise sink the eye into the floor
  if (!last.ONGROUND && !prev.ONGROUND) {
    v.PZ = last.PZ + (last.PZ - prev.PZ) * kk;
    v.VIEW_Z += (last.PZ - prev.PZ) * kk;     // the body's motion, not the eye's (a crouch in the air moves the eye 14)
  }
  v.predicted = true;
  return v;
}

async function frame() {
  if (!running || paused || document.hidden) {
    lastTic = performance.now();
    if (paused && renderer && last) { drawScene(renderer, hud, res, map.bsp, last, { faces: [], ents: [] }, { fov: settings.fov, state, playerModel: settings.model.split('/')[0] }); hud.drawCenter('paused', 80, 16); renderer.present(); }
    nextFrame();
    return;
  }
  try {
    const now = performance.now();
    const dt = frameAt ? Math.min(0.1, (now - frameAt) / 1000) : 0.016;
    frameAt = now;
    let ticked = 0, t;
    if (now - lastTic >= TIC_MS) {
      const tics = Math.min(2, Math.floor((now - lastTic) / TIC_MS));   // at most two tics a frame: better slow motion than a stall
      lastTic += tics * TIC_MS;
      if (now - lastTic > 200) lastTic = now;
      t = performance.now();
      prev = last;
      last = (await db.query('SELECT * FROM q3_tic(?, ?, ?, ?, ?, ?, ?, ?, ?)', readInput(tics), { rowMode: 'object' })).rows[0];
      perf.tic = performance.now() - t;
      ticked = tics;
      lastTics = tics;
      if (prev && !last.DEAD && !prev.DEAD && last.DUCKED !== prev.DUCKED) {
        // the eye drops 14 units or rises 14: spread it over 100 ms, carrying what is left of the last one
        const left = Math.max(0, 1 - (now - duckTime) / 100) * duckChange;
        duckChange = (last.DUCKED ? 14 : -14) + left;
        duckTime = now;
      }
      if (last.EXIT_KIND === 1 || last.EXIT_KIND === 3) {
        // the intermission is over: the next arena of the rotation, or this one again
        await startMap(last.EXIT_KIND === 1 && settings.rotate && last.NEXT_MAP ? last.NEXT_MAP : map.name);
        nextFrame();
        return;
      }
    }
    const alpha = Math.max(0, Math.min(1, (now - lastTic) / TIC_MS));
    const view = viewRow(alpha, now);

    t = performance.now();
    // one round trip: every row is tagged with what it is (see FRAME_ALL in sql/render.sql); the view is this frame's
    const wantSpeakers = ++frameNo % 10 === 0;
    curFov = last.MATCH_OVER || last.DEAD ? settings.fov : underwaterFov(zoomedFov(settings.fov, zoomed, now - zoomAt), view.WATERLEVEL, view.TIME_);
    audio.setUnderwater(view.WATERLEVEL >= 3 && !last.MATCH_OVER);   // the head under: muffled
    const fpv = firstPersonView(view, state, dt, curFov);   // the kicks, the dips, the bob, the zoom: the view that is painted
    // (its values bound, not written into the text: one statement the engine prepares once and keeps)
    const rows = (await db.query('SELECT * FROM frame_all(?, ?, ?, ?, ?, ?, ?, ?, ?, ?)',
      [settings.renderer === 'sql' ? 1 : 0, lastSoundId, lastFxId, wantSpeakers ? 1 : 0, fpv.x, fpv.y, fpv.z, fpv.yaw, fpv.pitch, curFov], arr)).rows;
    const fr = state.parse(rows);
    fr.sqlProjected = settings.renderer === 'sql';
    if (fr.eye) {
      // paint from the eye the faces were culled for (a prediction into a wall was clamped)
      const dz = fr.eye[2] - fpv.z;
      fpv.x = fr.eye[0]; fpv.y = fr.eye[1]; fpv.z = fr.eye[2];
      view.PX = fr.eye[0]; view.PY = fr.eye[1]; view.VIEW_Z += dz; view.PZ += dz;
    }
    if (ticked) { prevPose = curPose; curPose = poseOf(fr); }
    interpolateFrame(fr, alpha);
    if (fr.speakers) audio.setSpeakersOn(fr.speakers);
    if (fr.loopsAt.length) audio.moveSpeakers(fr.loopsAt);
    audio.setEntityLoops(fr.flying);
    perf.faces = performance.now() - t;
    perf.rows = fr.faces.length;
    const listener = { x: view.PX, y: view.PY, z: view.VIEW_Z, yaw: view.YAW };
    if (fr.sounds.length) { lastSoundId = fr.sounds[fr.sounds.length - 1][0]; audio.playEvents(fr.sounds, listener); }
    audio.update(listener);
    // CG_Player's flight loop while the flight powerup runs (the bots never take it)
    audio.setLoop('flight', 'sound/items/flight.wav', !last.DEAD && last.FLIGHT > 0);
    // CG_AddPlayerWeapon: the gun's firing sound while the trigger is held with ammunition (EF_FIRING: the lightning
    // gun's lg_hum, the gauntlet's fstrun), else its ready sound (the lightning gun's fsthum, the railgun's rg_hum,
    // the BFG's bfg_hum)
    audio.setLoop('weapon', weaponLoop(last.WEAPON, last.WEAPON_SOUND === 1), !last.DEAD && !last.MATCH_OVER && !!weaponLoop(last.WEAPON, last.WEAPON_SOUND === 1));
    if (fr.fx.length) { lastFxId = fr.fx[fr.fx.length - 1][0]; state.handleFx(renderer, fr.fx, view.TIME_, map.bsp); }
    if (ticked && (scoreboard || last.MATCH_OVER) && frameNo % 10 === 0) scores = (await db.query('SELECT * FROM scoreboard', [], arr)).rows;
    // the bots learn the arena while we play: a few grid columns, then a few nodes' edges, a tic (sql/waypoints.sql)
    if (ticked && map.unlinked > 0) {
      t = performance.now();
      map.unlinked = await buildWaypoints(db, perf.graph > 40 ? 1 : 3, perf.graph > 40 ? 1 : 2);   // smaller bites when a chunk ran long (q3dm7's 500 nodes)
      perf.graph = performance.now() - t;
      if (map.unlinked === 0) console.log(`[firebird-quake3] waypoint graph built: ${(await db.query('SELECT (SELECT COUNT(*) FROM waypoints) n, COUNT(*) e FROM wp_edges')).rows.map((r) => `${r.N} nodes, ${r.E} edges`)[0]}`);
    }

    t = performance.now();
    // the gun is put away past half-way into the zoom: drawn with the zoomed view it would fill the screen
    const noWeapon = curFov < (settings.fov + ZOOM_FOV) / 2;
    const portal = await portalFrame(fr, fpv, view.TIME_);
    const tint = drawScene(renderer, hud, res, map.bsp, view, fr, { fov: curFov, sqlProjected: fr.sqlProjected, state, dt, scoreboard, scores, view: fpv, noWeapon, portal, playerModel: settings.model.split('/')[0] });
    renderer.present(tint);
    perf.draw = performance.now() - t;
    updateStats(ticked);
    if (ticked) { const sp = $('spectate'), label = last.SPECTATOR ? 'Join' : 'Spectate'; if (sp.textContent !== label) sp.textContent = label; }
  } catch (err) {
    console.error(err);
    setStatus(`Error: ${err.message}`, true);
    running = false;
    return;
  }
  nextFrame();
}

let fpsT = performance.now(), fpsN = 0, fps = 0, ticN = 0, tps = 0;
function updateStats(ticked) {
  fpsN++; ticN += ticked;
  const now = performance.now();
  if (now - fpsT > 500) { fps = (fpsN * 1000) / (now - fpsT); tps = (ticN * 1000) / (now - fpsT); fpsT = now; fpsN = 0; ticN = 0; }
  statsEl.textContent = `${fps.toFixed(1)} fps · ${tps.toFixed(0)} tics/s · q3_tic ${perf.tic.toFixed(0)} ms · frame query ${perf.faces.toFixed(0)} ms (${perf.rows} faces) · paint ${perf.draw.toFixed(0)} ms · ${renderer.particles.length} particles${map?.unlinked > 0 ? ` · bots mapping the arena (${map.unlinked} to go, ${perf.graph.toFixed(0)} ms)` : ''}`;
}

/** A portal or a mirror on screen and near enough to see through: what its camera sees (FRAME_PORTAL), as drawScene takes it */
async function portalFrame(fr, fpv, time) {
  if (!map.portals?.length || settings.renderer === 'sql') return null;
  // the nearest on screen (one view a frame)
  const dist = (p) => { const c = p.center ?? p.origin; return Math.hypot(fpv.x - c[0], fpv.y - c[1], fpv.z - c[2]); };
  for (const p of [...map.portals].sort((a, b) => dist(a) - dist(b))) {
    if (!fr.faces.some((f) => p.faces.has(f[0]))) continue;
    const fade = portalFade(p, fpv);
    if (fade <= 0) return { faces: p.faces, fade: 0, frame: null };
    const v = (p.mirror ? mirrorView : portalView)(p, { ...fpv, fov: curFov }, time);
    const rows = (await db.query(`SELECT * FROM frame_portal(${v.x}, ${v.y}, ${v.z}, ${v.fwd.join(', ')}, ${v.right.join(', ')}, ${v.up.join(', ')}, ${curFov}, ${v.pvs.join(', ')}, ${(v.clip ?? [0, 0, 0, 0]).join(', ')})`, [], arr)).rows;
    const frame = portalState.parse(rows);
    return { faces: p.faces, fade, frame, view: v, brushAngles: portalState.brushAngles };
  }
  return null;
}

// ── SQL console ─────────────────────────────────────────────────────────
async function runConsole() {
  const sqlText = $('sql').value.trim();
  if (!sqlText || !db) return;
  const out = $('sql-out');
  const t0 = performance.now();
  try {
    const r = /^\s*(select|with|execute\s+block)/i.test(sqlText)
      ? await db.query(sqlText, [], { rowMode: 'object' })
      : { rows: [], exec: await db.exec(sqlText) };
    const ms = (performance.now() - t0).toFixed(1);
    if (!r.rows.length) { out.textContent = `OK (${ms} ms)`; return; }
    const cols = Object.keys(r.rows[0]);
    const lines = [cols.join('\t'), ...r.rows.slice(0, 200).map((row) => cols.map((c) => fmt(row[c])).join('\t'))];
    out.textContent = `${r.rows.length} row(s), ${ms} ms\n${lines.join('\n')}`;
  } catch (err) {
    out.textContent = err.message;
  }
}
const fmt = (v) => (typeof v === 'number' && !Number.isInteger(v) ? v.toFixed(2) : String(v));
$('run-sql').addEventListener('click', runConsole);
$('sql').addEventListener('keydown', (e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) runConsole(); });
for (const b of document.querySelectorAll('[data-sql]')) b.addEventListener('click', () => { $('sql').value = b.dataset.sql; runConsole(); });

// ── boot ────────────────────────────────────────────────────────────────
async function openDatabase() {
  // (the engine is firebird-wasm's build without threads: it needs no SharedArrayBuffer, so no cross-origin
  // isolation and no reload to get it; a page built with FB_THREADS=1 loads the threaded one, which does)
  setStatus('Starting Firebird 6 (WebAssembly)…');
  const instance = new FirebirdBrowser('memory://quake3', {
    worker: new Worker(new URL('./firebird-engine-worker.js', import.meta.url)),
    multiTab: 'allow-unsafe',
    autoPersist: false,
  });
  const v = await instance.query("SELECT rdb$get_context('SYSTEM', 'ENGINE_VERSION') AS v FROM rdb$database");
  $('engine').textContent = `Firebird ${v.rows[0].V}`;
  setStatus('Creating the Quake III schema (PSQL)…');
  await createSchema(instance, { schema: schemaSql, physics: physicsSql, game: gameSql, waypoints: waypointsSql, player: playerSql, bots: botsSql, render: renderSql });
  return instance;
}

// the painter: the software rasteriser, or the WebGL port of Quake III's renderer (the HUD then goes on the overlay canvas)
function makeRenderer() {
  const gl = settings.renderer === 'gl';
  try { renderer = gl ? new GLRenderer(glCanvas, res, overlayCanvas) : new Renderer(canvas, res); }
  catch (err) { setStatus(err.message, true); settings.renderer = 'fast'; $('renderer').value = 'fast'; renderer = new Renderer(canvas, res); }
  const isGl = renderer instanceof GLRenderer;
  overlayCanvas.hidden = !isGl; glCanvas.hidden = !isGl; canvas.hidden = isGl;
  renderer.setSize(viewWidth(), viewHeight());
  renderer.setBrightness(settings.brightness);
  renderer.setResources(res);
  if (map) renderer.setSky(map.sky);
  hud = new Hud(pak, renderer);
}

async function usePak(buffer, label) {
  running = false;
  setStatus(`Opening ${label}…`);
  basePak = new Pk3(buffer);
  basePak.label = label;
  pak = new PakSet([basePak]);
  local.clear(); localPaks.length = 0;
  const maps = pak.mapNames();
  if (!maps.length) throw new Error(`${label} has no maps`);
  pak.inflateAll((n) => !/^(demos|video|vm|botfiles|menu|levelshots)\//.test(n) && !n.endsWith('.aas'));
  setStatus(`Copying ${label} models into Firebird…`);
  res = await loadResources(db, pak, { width: viewWidth(), height: viewHeight(), fov: settings.fov });
  makeRenderer();
  audio.setPak(pak);
  fillMapMenu();
  $('pakname').textContent = label;
  const first = maps.includes(settings.map) || catalog.some((c) => c.map === settings.map) ? settings.map : maps.includes('q3dm1') ? 'q3dm1' : maps[0];
  await startMap(first);
}

async function boot() {
  try {
    db = await openDatabase();
    window.quake3 = { db, audio, sql: (q, p) => db.query(q, p).then((r) => r.rows) };
    // the map packs the site hosts, if it does (none in a checkout without npm run fetch-maps)
    try {
      const ix = await fetch(new URL('./pak/maps/index.json', location.href));
      if (ix.ok) catalog = await ix.json();
    } catch { catalog = []; }
    setStatus('Downloading pak0.pk3 (the Quake III Arena demo, 45 MB)…');
    const resp = await fetch(new URL('./pak/pak0.pk3', location.href));
    if (!resp.ok) throw new Error(`could not fetch pak0.pk3 (${resp.status}); pick a PK3 file instead`);
    await usePak(await resp.arrayBuffer(), 'pak0.pk3');
    nextFrame();
  } catch (err) {
    console.error(err);
    setStatus(err.message, true);
  }
}

// pk3s from disk: a game's base pak (it has player models: the full game's pak0) replaces the demo; any other
// (map packs: OpenArena's, the Community Map-Pack, any Quake III map) goes on top and its maps join the menu
$('pakfile').addEventListener('change', async (e) => {
  const files = [...e.target.files];
  if (!files.length || !db) return;
  try {
    let firstNew = null;
    for (const f of files) {
      const pk3 = new Pk3(await f.arrayBuffer());
      pk3.label = f.name;
      if (pk3.list('models/players/').length && pk3.mapNames().length) { await usePak(await f.arrayBuffer(), f.name); continue; }
      localPaks.push(pk3);
      for (const m of pk3.mapNames()) { local.set(m, f.name); firstNew ??= m; }
    }
    stackPaks();
    $('pakname').textContent = localPaks.length ? `${basePak.label} + ${localPaks.length} of yours` : basePak.label;
    fillMapMenu();
    if (firstNew) { settings.map = firstNew; saveSettings(); await startMap(firstNew); }
  } catch (err) { setStatus(err.message, true); }
});
$('map').addEventListener('change', (e) => { settings.map = e.target.value; saveSettings(); startMap(e.target.value).catch((err) => setStatus(err.message, true)); });
$('detail').value = settings.detail;
$('detail').addEventListener('change', async (e) => {
  settings.detail = e.target.value; saveSettings();
  await setView(db, viewWidth(), viewHeight(), settings.fov);
  renderer.setSize(viewWidth(), viewHeight());
});
$('renderer').value = settings.renderer;
$('renderer').addEventListener('change', (e) => {
  const wasGl = settings.renderer === 'gl';
  settings.renderer = e.target.value; saveSettings();
  if (res && (wasGl || settings.renderer === 'gl')) { renderer = null; makeRenderer(); }
});
$('brightness').value = String(settings.brightness);
$('brightness').addEventListener('change', (e) => { settings.brightness = Number(e.target.value); saveSettings(); if (renderer) renderer.setBrightness(settings.brightness); });
// spectating: out of the match and back (SetTeam)
$('spectate').addEventListener('click', () => {
  if (!db || !running || !last) return;
  db.exec(`EXECUTE PROCEDURE set_spectator(${last.SPECTATOR ? 0 : 1})`).catch((err) => console.error(err));
});
// bots joining and leaving mid-game (G_AddBot, clientkick): the menus offer the roster's absent ones and
// those in the arena
async function refreshBotMenus() {
  if (!db) return;
  const roster = (await db.query('SELECT name FROM bot_defs ORDER BY name')).rows.map((r) => r.NAME.trim());
  const here = new Set((await db.query("SELECT bot FROM ents WHERE classname = 'bot'")).rows.map((r) => r.BOT.trim()));
  const fill = (sel, label, names) => sel.replaceChildren(new Option(label, ''), ...names.map((n) => new Option(n, n)));
  fill($('addbot'), 'Add bot…', roster.filter((n) => !here.has(n)));
  fill($('kickbot'), 'Remove bot…', roster.filter((n) => here.has(n)));
}
const botMenu = (id, sql) => {
  $(id).addEventListener('focus', () => refreshBotMenus().catch((err) => console.error(err)));
  $(id).addEventListener('change', async (e) => {
    const n = e.target.value;
    e.target.value = '';
    if (!n || !db || !running) return;
    try { await db.exec(sql(n)); } catch (err) { console.error(err); }
    await refreshBotMenus();
  });
};
botMenu('addbot', (n) => `EXECUTE PROCEDURE add_bot('${n}', ${settings.skill | 0})`);
botMenu('kickbot', (n) => `EXECUTE PROCEDURE kick_bot('${n}')`);
// the name the bots call you by
const cleanName = (s) => String(s ?? '').replace(/[^\x20-\x7e]/g, '').replace(/'/g, '').trim().slice(0, 15) || 'Player';
async function setPlayerName() { if (db) await db.exec(`UPDATE player SET name = '${cleanName(settings.name)}' WHERE id = 1`); }
$('pname').value = settings.name;
// the model played (the "model" userinfo, model/skin): the pak's player models and their own skins, not the team ones
function fillModelMenu() {
  const opts = [];
  for (const [pm, p] of [...res.players].sort(([a], [b]) => a.localeCompare(b))) {
    for (const sk of [...p.skins.keys()].sort((a, b) => (a === 'default' ? -1 : b === 'default' ? 1 : a.localeCompare(b)))) {
      if (sk !== 'red' && sk !== 'blue') opts.push(`${pm}/${sk}`);
    }
  }
  if (!opts.includes(settings.model)) settings.model = opts.includes('sarge/default') ? 'sarge/default' : opts[0] ?? 'sarge/default';
  $('pmodel').replaceChildren(...opts.map((o) => new Option(o.endsWith('/default') ? o.slice(0, -8) : o, o)));
  $('pmodel').value = settings.model;
}
async function setPlayerModel() {
  const [m, s] = settings.model.split('/');
  if (db) await db.exec(`EXECUTE PROCEDURE set_player_model('${m.replace(/[^a-z0-9_]/gi, '')}', '${(s ?? 'default').replace(/[^a-z0-9_]/gi, '')}')`);
}
// the rail's colour (the "color1" userinfo), as the setup menu orders them (uitogamecode)
const RAIL_COLORS = [[4, 'red'], [6, 'yellow'], [2, 'green'], [3, 'teal'], [1, 'blue'], [5, 'magenta'], [7, 'white']];
$('prail').replaceChildren(...RAIL_COLORS.map(([v, n]) => new Option(n, String(v))));
if (!RAIL_COLORS.some(([v]) => v === Number(settings.rail))) settings.rail = 4;
$('prail').value = String(settings.rail);
async function setPlayerColor() {
  if (db) await db.exec(`EXECUTE PROCEDURE set_player_color(${Number(settings.rail) | 0})`);
}
$('prail').addEventListener('change', (e) => { settings.rail = Number(e.target.value); saveSettings(); setPlayerColor().catch((err) => console.error(err)); });
$('pmodel').addEventListener('change', (e) => { settings.model = e.target.value; saveSettings(); setPlayerModel().catch((err) => console.error(err)); });
$('pname').addEventListener('change', (e) => { settings.name = cleanName(e.target.value); e.target.value = settings.name; saveSettings(); setPlayerName().catch((err) => console.error(err)); });
// the limits apply at once, as Quake III's fraglimit and timelimit cvars do
const setLimit = (key, col) => (e) => {
  settings[key] = Number(e.target.value); saveSettings();
  if (db && running) db.exec(`UPDATE game SET ${col} = ${settings[key] | 0} WHERE id = 1`).catch((err) => console.error(err));
};
$('fraglimit').value = String(settings.fraglimit);
$('fraglimit').addEventListener('change', setLimit('fraglimit', 'fraglimit'));
$('timelimit').value = String(settings.timelimit);
$('timelimit').addEventListener('change', setLimit('timelimit', 'timelimit'));
$('rotate').checked = settings.rotate;
$('rotate').addEventListener('change', (e) => { settings.rotate = e.target.checked; saveSettings(); });
$('predict').checked = settings.predict;
$('predict').addEventListener('change', (e) => { settings.predict = e.target.checked; saveSettings(); });
$('skill').value = String(settings.skill);
$('skill').addEventListener('change', (e) => { settings.skill = Number(e.target.value); saveSettings(); });
$('gametype').value = settings.gametype;
$('gametype').addEventListener('change', (e) => { settings.gametype = e.target.value; saveSettings(); });
$('team').value = settings.team;
$('team').addEventListener('change', (e) => { settings.team = e.target.value; saveSettings(); });
$('bots').value = String(settings.bots);
$('bots').addEventListener('change', (e) => { settings.bots = Number(e.target.value); saveSettings(); });
$('sfxvol').value = settings.sfx;
$('sfxvol').addEventListener('input', () => { settings.sfx = Number($('sfxvol').value); saveSettings(); audio.unlock(); audio.setVolume(settings.sfx / 100); });
$('music').value = settings.musicMode;
$('music').addEventListener('change', (e) => { settings.musicMode = e.target.value; saveSettings(); audio.unlock(); audio.setMusicMode(settings.musicMode); });
$('musicvol').value = settings.music;
$('musicvol').addEventListener('input', () => { settings.music = Number($('musicvol').value); saveSettings(); audio.setMusicVolume(settings.music / 100); });

boot();
